import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// Site ayarları collection
export const SETTINGS_COLLECTION = 'siteSettings';
export const SETTINGS_DOC_ID = 'main'; // Admin panelle uyumlu hale getir

// Site ayarları interface
export interface SiteSettings {
  // Logo ve Branding
  siteName: string;
  logoUrl: string;

  // Hakkımızda İçeriği
  aboutUs: {
    title: string;
    content: string;
  };
  
  // Hero Slider
  sliderImages: {
    id: string;
    imageUrl: string;
    title: string;
    description: string;
    order: number;
  }[];

  // Galeri
  gallery: {
    id: string;
    imageUrl: string;
    title: string;
    description?: string;
    createdAt: number; // Timestamp for sorting (newest first)
  }[];

  // Form Ayarları
  forms: {
    contactForm: {
      enabled: boolean;
      recipientEmail: string;
      subject: string;
      successMessage: string;
      errorMessage: string;
    };
    preOrderForm: {
      enabled: boolean;
      recipientEmail: string;
      subject: string;
      successMessage: string;
      errorMessage: string;
    };
    emailService: {
      provider: 'emailjs' | 'smtp' | 'disabled';
      // EmailJS ayarları
      emailJsServiceId?: string;
      emailJsTemplateId?: string;
      emailJsPublicKey?: string;
      // SMTP ayarları (gelecekte eklenebilir)
      smtpHost?: string;
      smtpPort?: number;
      smtpUser?: string;
      smtpPassword?: string;
    };
  };
  
  // Sosyal Medya
  socialMedia: {
    facebook: string;
    instagram: string;
    whatsapp: string;
    twitter?: string;
    linkedin?: string;
  };
  
  // İletişim Bilgileri
  contact: {
    phone: string;
    phone2: string;
    landLine: string;
    email: string;
    address: string;
    city: string;
    country: string;
    workingHours: string;
  };
  
  // Footer
  footer: {
    copyright: string;
    description: string;
    quickLinks: {
      title: string;
      url: string;
    }[];
  };
  
  // SEO
  seo: {
    metaTitle: string;
    metaDescription: string;
    keywords: string;
  };
  
  // Güncelleme bilgileri
  updatedAt?: Timestamp;
  createdAt?: Timestamp;
}

// Varsayılan site ayarları
export const defaultSiteSettings: SiteSettings = {
  siteName: 'Galaraf',
  logoUrl: '/logo.png',
  aboutUs: {
    title: '',
    content: ''
  },
  
  sliderImages: [],

  gallery: [],

  forms: {
    contactForm: {
      enabled: true,
      recipientEmail: '',
      subject: 'Yeni İletişim Formu Mesajı',
      successMessage: 'Mesajınız başarıyla gönderildi. En kısa sürede size dönüş yapacağız.',
      errorMessage: 'Mesajınız gönderilirken bir hata oluştu. Lütfen tekrar deneyiniz.'
    },
    preOrderForm: {
      enabled: true,
      recipientEmail: '',
      subject: 'Yeni Ön Sipariş Talebi',
      successMessage: 'Ön sipariş talebiniz başarıyla gönderildi. Size en kısa sürede dönüş yapacağız.',
      errorMessage: 'Sipariş talebiniz gönderilirken bir hata oluştu. Lütfen tekrar deneyiniz.'
    },
    emailService: {
      provider: 'disabled'
    }
  },
  
  socialMedia: {
    facebook: 'https://facebook.com/galaraf',
    instagram: 'https://instagram.com/galaraf',
    whatsapp: '+90 555 123 45 67',
    twitter: 'https://twitter.com/galaraf'
  },
  
  contact: {
    phone: '+90 212 123 45 67',
    phone2: '+90 212 123 45 67',
    landLine: '+90 212 123 45 67',
    email: 'info@galaraf.com',
    address: 'Sanayi Mahallesi, Raf Sokak No:123',
    city: 'İstanbul',
    country: 'Türkiye',
    workingHours: 'Pazartesi - Cuma: 08:00 - 18:00'
  },
  
  footer: {
    copyright: '© 2024 Galaraf. Tüm hakları saklıdır.',
    description: 'Kaliteli raf ve depolama sistemleri üreticisi',
    quickLinks: [
      { title: 'Hakkımızda', url: '/hakkimizda' },
      { title: 'İletişim', url: '/contact' },
      { title: 'Ürünler', url: '/products' },
      { title: 'Galeri', url: '/gallery' }
    ]
  },
  
  seo: {
    metaTitle: 'Galaraf - Profesyonel Raf ve Depolama Sistemleri',
    metaDescription: 'Kaliteli raf, depo ve endüstriyel depolama çözümleri. Profesyonel raf sistemleri üreticisi.',
    keywords: 'raf, depo, raf sistemleri, endüstriyel raf, depolama, galaraf'
  }
};

// Site ayarları service
export const siteSettingsService = {
  // Site ayarlarını getir
  async get(): Promise<SiteSettings> {
    try {
      const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as SiteSettings;
        // Geriye uyumluluk: gallery alanı yoksa ekle
        if (!data.gallery) {
          data.gallery = [];
        }
        // Geriye uyumluluk: forms alanı yoksa ekle
        if (!data.forms) {
          data.forms = defaultSiteSettings.forms;
        } 
        return data;
      } else {
        // İlk kez yükleniyorsa varsayılan ayarları oluştur
        await this.create(defaultSiteSettings);
        return defaultSiteSettings;
      }
    } catch (error) {
      console.error('❌ Site ayarları getirilemedi:', error);
      // Hata durumunda varsayılan ayarları döndür
      return defaultSiteSettings;
    }
  },

  // Site ayarlarını oluştur
  async create(settings: SiteSettings): Promise<void> {
    try {
      const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
      const settingsData = {
        ...settings,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      await setDoc(docRef, settingsData);
    } catch (error) {
      console.error('❌ Site ayarları oluşturulamadı:', error);
      throw new Error('Site ayarları oluşturulamadı');
    }
  },

  // Site ayarlarını güncelle
  async update(updates: Partial<SiteSettings>): Promise<void> {
    try {
      const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now()
      };

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('❌ Site ayarları güncellenemedi:', error);
      throw new Error('Site ayarları güncellenemedi');
    }
  },

  // Slider'a yeni resim ekle
  async addSliderImage(image: Omit<SiteSettings['sliderImages'][0], 'id'>): Promise<void> {
    try {
      const settings = await this.get();
      const newImage = {
        ...image,
        id: `slider-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      };
      
      const updatedSliderImages = [...settings.sliderImages, newImage];
      await this.update({ sliderImages: updatedSliderImages });
      
    } catch (error) {
      console.error('❌ Slider resmi eklenemedi:', error);
      throw error;
    }
  },

  // Slider'dan resim sil
  async removeSliderImage(imageId: string): Promise<void> {
    try {
      const settings = await this.get();
      const updatedSliderImages = settings.sliderImages.filter(img => img.id !== imageId);
      await this.update({ sliderImages: updatedSliderImages });
      
    } catch (error) {
      console.error('❌ Slider resmi silinemedi:', error);
      throw error;
    }
  },

  // Quick link ekle
  async addQuickLink(link: { title: string; url: string }): Promise<void> {
    try {
      const settings = await this.get();
      const updatedQuickLinks = [...settings.footer.quickLinks, link];
      
      await this.update({
        footer: {
          ...settings.footer,
          quickLinks: updatedQuickLinks
        }
      });
      
    } catch (error) {
      console.error('❌ Hızlı link eklenemedi:', error);
      throw error;
    }
    },

  // Galeri resmi ekle
  async addGalleryImage(image: Omit<SiteSettings['gallery'][0], 'id' | 'createdAt'>): Promise<void> {
    try {
      const settings = await this.get();
      const newImage = {
        ...image,
        id: `gallery-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        createdAt: Date.now() // Timestamp for sorting
      };
      
      // En yeniler başta olsun (reverse chronological)
      // settings.gallery undefined olabilir, güvenlik kontrolü yap
      const currentGallery = settings.gallery || [];
      const updatedGallery = [newImage, ...currentGallery];
      await this.update({ gallery: updatedGallery });
      
    } catch (error) {
      console.error('❌ Galeri resmi eklenemedi:', error);
      throw error;
    }
  },

  // Galeri resmi sil
  async removeGalleryImage(imageId: string): Promise<void> {
    try {
      const settings = await this.get();
      // settings.gallery undefined olabilir, güvenlik kontrolü yap
      const currentGallery = settings.gallery || [];
      const updatedGallery = currentGallery.filter(img => img.id !== imageId);
      await this.update({ gallery: updatedGallery });
      
    } catch (error) {
      console.error('❌ Galeri resmi silinemedi:', error);
      throw error;
    }
  }
};